import React, { useEffect, useMemo, useState } from "react";
import Icons from "../icons";
import TicketLinkField from "./TicketLinkField";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/**
 * Modal to link/unlink an execution to Jira tickets (Testing status).
 *
 * Loads the execution's currently-linked ticket anchors, lets the user add or
 * remove tickets via the shared TicketLinkField, then syncs the changes:
 *   - newly added tickets  -> POST   /api/executions/{key}/tickets
 *   - removed tickets      -> DELETE /api/executions/{key}/tickets/{ticketkey}
 *
 * Props:
 *   executionkey, executionName, userkey, onClose, onSaved
 */
export default function ExecutionTicketsModal({
  executionkey,
  executionName,
  userkey,
  onClose,
  onSaved,
}) {
  // Selected tickets, shape: [{ ticketkey, ticket_key, summary }]
  const [value, setValue] = useState([]);
  const [initial, setInitial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}executions/${encodeURIComponent(executionkey)}/tickets`
        );
        const body = await res.json().catch(() => ({}));
        const rows = (Array.isArray(body?.data) ? body.data : []).map((t) => ({
          ticketkey: t.ticketkey,
          ticket_key: t.ticket_key,
          summary: t.summary || "",
        }));
        if (active) {
          setValue(rows);
          setInitial(rows);
        }
      } catch {
        if (active) setError("Could not load linked tickets.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [executionkey]);

  const { added, removed } = useMemo(() => {
    const initKeys = new Set(initial.map((t) => t.ticketkey));
    const curKeys = new Set(value.map((t) => t.ticketkey));
    return {
      added: value.filter((t) => !initKeys.has(t.ticketkey)).map((t) => t.ticketkey),
      removed: initial.filter((t) => !curKeys.has(t.ticketkey)).map((t) => t.ticketkey),
    };
  }, [value, initial]);

  const dirty = added.length > 0 || removed.length > 0;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      if (added.length) {
        const res = await fetch(
          `${API_BASE_URL}executions/${encodeURIComponent(executionkey)}/tickets`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ticket_keys: added }),
          }
        );
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b?.detail || `HTTP ${res.status}`);
        }
      }
      for (const ticketkey of removed) {
        await fetch(
          `${API_BASE_URL}executions/${encodeURIComponent(
            executionkey
          )}/tickets/${encodeURIComponent(ticketkey)}`,
          { method: "DELETE" }
        );
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message || "Could not save ticket links.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className={`flex ${DIALOG_HEIGHT} w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <Icons.Boards className="h-4 w-4 text-brand-600" />
              Link tickets
            </h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">
              For execution <span className="font-medium text-slate-700">{executionName}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <Icons.Spinner className="h-4 w-4 animate-spin" />
              Loading linked tickets…
            </div>
          ) : (
            <>
              <p className="mb-3 text-xs text-slate-500">
                Pick Jira tickets in Testing status to link this execution to. Use the
                × on a chip to remove a link. Changes apply when you save.
              </p>
              <TicketLinkField
                boardId={null}
                userkey={userkey}
                value={value}
                onChange={setValue}
              />
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            </>
          )}
        </div>

        <div className="flex flex-none items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || !dirty}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <>
                <Icons.Spinner className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Icons.Check className="h-4 w-4" />
                Save links
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
